import React, { useState, useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  Typography, CircularProgress, Tooltip, IconButton,
  Grid, Alert, Chip, Tabs, Tab,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import VisibilityIcon from '@mui/icons-material/Visibility';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { PageIndices, TabIndices } from '../../common/i-doc/i-doc.registry.ts';
import { DashboardLayout } from '@components/layout/DashboardLayout.tsx';
import { PageCard } from '@components/common/PageCard.tsx';
import { PageToolbar } from '@components/common/PageToolbar.tsx';
import { UniversalPagination } from '@components/common/UniversalPagination.tsx';
import { PageHint } from '@components/common/PageHint.tsx';
import { toastService } from '@services/toast.service.ts';
import {
  adminAiReportsService,
  AiReport,
  AiReportsListParams,
} from '@services/admin-ai-reports.service.ts';
import { AI_REPORT_TYPE_LABELS } from '@/types/client-reports';

// ---------- Types ----------

interface DetailModalState {
  open: boolean;
  report: AiReport | null;
  loading: boolean;
  tabIndex: number;
}

// ---------- Main Component ----------

const AdminAiReportsPageContent: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [reports, setReports] = useState<AiReport[]>([]);
  const [total, setTotal] = useState(0);

  // Filters
  const [clientId, setClientId] = useState('');
  const [userId, setUserId] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  // Pagination
  const limit = parseInt(searchParams.get('limit') || '50');
  const page = parseInt(searchParams.get('page') || '1');
  const offset = (page - 1) * limit;

  // Detail Modal
  const [detailModal, setDetailModal] = useState<DetailModalState>({
    open: false,
    report: null,
    loading: false,
    tabIndex: 0,
  });

  // Fetch reports
  const fetchReports = useCallback(async () => {
    setLoading(true);
    try {
      const params: AiReportsListParams = {
        limit,
        offset,
        ...(clientId && { clientId: parseInt(clientId) }),
        ...(userId && { initiatedByUserId: parseInt(userId) }),
        ...(dateFrom && { dateFrom }),
        ...(dateTo && { dateTo }),
      };

      const response = await adminAiReportsService.getReports(params);
      setReports(response.data);
      setTotal(response.meta.total);
    } catch (error: any) {
      toastService.error(error?.message || 'Помилка при завантаженні звітів');
    } finally {
      setLoading(false);
    }
  }, [limit, offset, clientId, userId, dateFrom, dateTo]);

  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  // Pagination handlers
  const handlePageChange = (newPage: number) => {
    setSearchParams({ limit: limit.toString(), page: newPage.toString() });
  };

  const handleLimitChange = (newLimit: number) => {
    setSearchParams({ limit: newLimit.toString(), page: '1' });
  };

  // Detail modal handlers
  const openDetailModal = (report: AiReport) => {
    setDetailModal({ open: true, report, loading: false, tabIndex: 0 });
  };

  const closeDetailModal = () => {
    setDetailModal({ open: false, report: null, loading: false, tabIndex: 0 });
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setDetailModal((prev) => ({ ...prev, tabIndex: newValue }));
  };

  const handleDownloadPdf = async (reportId: number) => {
    try {
      await adminAiReportsService.downloadPdf(reportId);
      toastService.success('PDF успішно завантажено');
    } catch (error: any) {
      toastService.error(error?.message || 'Помилка при завантаженні PDF');
    }
  };

  // Reset filters
  const handleResetFilters = () => {
    setClientId('');
    setUserId('');
    setDateFrom('');
    setDateTo('');
    setSearchParams({ limit: '50', page: '1' });
  };

  const statusColors = {
    completed: 'success' as const,
    pending: 'warning' as const,
    failed: 'error' as const,
  };

  return (
    <Box>
      <PageHint iDoc={PageIndices.ADMIN_AI_REPORTS} />

      <PageCard>
        <PageToolbar
          filters={[
            {
              id: 'clientId',
              type: 'text',
              label: 'ID клієнта',
              value: clientId,
              onChange: setClientId,
            },
            {
              id: 'userId',
              type: 'text',
              label: 'ID користувача (ініціатор)',
              value: userId,
              onChange: setUserId,
            },
            {
              id: 'dateFrom',
              type: 'text',
              label: 'Дата з (YYYY-MM-DD)',
              value: dateFrom,
              onChange: setDateFrom,
            },
            {
              id: 'dateTo',
              type: 'text',
              label: 'Дата по (YYYY-MM-DD)',
              value: dateTo,
              onChange: setDateTo,
            },
          ]}
          onReset={handleResetFilters}
          storageKey="filters:admin-ai-reports"
        />

        {loading && <CircularProgress sx={{ display: 'block', mx: 'auto', my: 2 }} />}

        {!loading && reports.length === 0 && (
          <Alert severity="info" sx={{ mt: 2 }}>
            Звіти не знайдено
          </Alert>
        )}

        {!loading && reports.length > 0 && (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: 'action.hover' }}>
                  <TableCell>ID</TableCell>
                  <TableCell>Тип</TableCell>
                  <TableCell>Клієнт ID</TableCell>
                  <TableCell>Користувач ID</TableCell>
                  <TableCell>Період</TableCell>
                  <TableCell>Мова</TableCell>
                  <TableCell>Статус</TableCell>
                  <TableCell>Створено</TableCell>
                  <TableCell align="right">Дія</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {reports.map((report) => (
                  <TableRow key={report.id} hover>
                    <TableCell>{report.id}</TableCell>
                    <TableCell>{AI_REPORT_TYPE_LABELS[report.reportType] ?? report.reportType}</TableCell>
                    <TableCell>{report.clientId}</TableCell>
                    <TableCell>{report.initiatedByUserId}</TableCell>
                    <TableCell>
                      {report.periodFrom} → {report.periodTo}
                    </TableCell>
                    <TableCell>{report.locale}</TableCell>
                    <TableCell>
                      <Chip
                        label={report.status}
                        color={statusColors[report.status]}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Tooltip title={report.createdAt}>
                        <span>{new Date(report.createdAt).toLocaleDateString('uk-UA')}</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Завантажити PDF">
                        <IconButton
                          size="small"
                          onClick={() => handleDownloadPdf(report.id)}
                          disabled={loading || report.status !== 'completed'}
                        >
                          <FileDownloadIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Переглянути деталі">
                        <IconButton
                          size="small"
                          onClick={() => openDetailModal(report)}
                          disabled={loading}
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <UniversalPagination
          page={page}
          count={Math.ceil(total / limit)}
          limit={limit}
          total={total}
          onPageChange={handlePageChange}
          onLimitChange={handleLimitChange}
        />
      </PageCard>

      {/* Detail Modal */}
      <Dialog open={detailModal.open} onClose={closeDetailModal} maxWidth="md" fullWidth>
        <DialogTitle>
          Деталі AI-звіту #{detailModal.report?.id}
          <Tooltip title="Всі дані звіту для аналізу якості">
            <InfoOutlinedIcon
              sx={{ fontSize: 18, ml: 1, verticalAlign: 'middle', cursor: 'help' }}
            />
          </Tooltip>
        </DialogTitle>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={detailModal.tabIndex} onChange={handleTabChange}>
            <Tab label="Інформація" />
            <Tab label="Контекст" />
            <Tab label="Промпт" />
            <Tab label="Результат" disabled={!detailModal.report?.response} />
          </Tabs>
        </Box>
        <DialogContent>
          {detailModal.report && (
            <Box sx={{ pt: 2 }}>
              {/* Tab 0: Information */}
              {detailModal.tabIndex === 0 && (
                <Box>
                  <PageHint iDoc={TabIndices.ADMIN_AI_REPORT_INFO} />
                  <Grid container spacing={2} sx={{ mt: 1 }}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="textSecondary">
                        ID звіту
                      </Typography>
                      <Typography>{detailModal.report.id}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="textSecondary">
                        Тип
                      </Typography>
                      <Typography>{AI_REPORT_TYPE_LABELS[detailModal.report.reportType] ?? detailModal.report.reportType}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="textSecondary">
                        Клієнт ID
                      </Typography>
                      <Typography>{detailModal.report.clientId}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="textSecondary">
                        Ініціатор (User ID)
                      </Typography>
                      <Typography>{detailModal.report.initiatedByUserId}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="textSecondary">
                        Статус
                      </Typography>
                      <Chip
                        label={detailModal.report.status}
                        color={statusColors[detailModal.report.status]}
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="textSecondary">
                        Період
                      </Typography>
                      <Typography>
                        {detailModal.report.periodFrom} → {detailModal.report.periodTo}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="textSecondary">
                        Мова
                      </Typography>
                      <Typography>{detailModal.report.locale}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="textSecondary">
                        Створено
                      </Typography>
                      <Typography>
                        {new Date(detailModal.report.createdAt).toLocaleString('uk-UA')}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="textSecondary">
                        Оновлено
                      </Typography>
                      <Typography>
                        {new Date(detailModal.report.updatedAt).toLocaleString('uk-UA')}
                      </Typography>
                    </Grid>
                  </Grid>

                  {detailModal.report.sections && (
                    <Box sx={{ mt: 3 }}>
                      <Typography variant="subtitle2" color="textSecondary">
                        Секції
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                        {detailModal.report.sections.map((section, idx) => (
                          <Chip key={idx} label={section} size="small" variant="outlined" />
                        ))}
                      </Box>
                    </Box>
                  )}

                  {detailModal.report.errorMessage && (
                    <Alert severity="error" sx={{ mt: 3 }}>
                      {detailModal.report.errorMessage}
                    </Alert>
                  )}
                </Box>
              )}

              {/* Tab 1: Context */}
              {detailModal.tabIndex === 1 && (
                <Box>
                  <PageHint iDoc={TabIndices.ADMIN_AI_REPORT_CONTEXT} />
                  <Box
                    sx={{
                      bg: 'background.paper',
                      p: 2,
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: 'divider',
                      maxHeight: 400,
                      overflow: 'auto',
                      mt: 2,
                    }}
                  >
                    <Typography
                      component="pre"
                      sx={{ fontFamily: 'monospace', fontSize: 12, whiteSpace: 'pre-wrap' }}
                    >
                      {detailModal.report.context}
                    </Typography>
                  </Box>
                </Box>
              )}

              {/* Tab 2: Prompt */}
              {detailModal.tabIndex === 2 && (
                <Box>
                  <PageHint iDoc={TabIndices.ADMIN_AI_REPORT_PROMPT} />
                  <Box
                    sx={{
                      bg: 'background.paper',
                      p: 2,
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: 'divider',
                      maxHeight: 400,
                      overflow: 'auto',
                      mt: 2,
                    }}
                  >
                    <Typography
                      component="pre"
                      sx={{ fontFamily: 'monospace', fontSize: 12, whiteSpace: 'pre-wrap' }}
                    >
                      {detailModal.report.prompt}
                    </Typography>
                  </Box>
                </Box>
              )}

              {/* Tab 3: Response */}
              {detailModal.tabIndex === 3 && detailModal.report.response && (
                <Box>
                  <PageHint iDoc={TabIndices.ADMIN_AI_REPORT_RESPONSE} />
                  <Box
                    sx={{
                      bg: 'background.paper',
                      p: 2,
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: 'divider',
                      maxHeight: 400,
                      overflow: 'auto',
                      mt: 2,
                    }}
                  >
                    <div dangerouslySetInnerHTML={{ __html: detailModal.report.response }} />
                  </Box>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDetailModal}>Закрити</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export const AdminAiReportsPage: React.FC = () => (
  <DashboardLayout>
    <AdminAiReportsPageContent />
  </DashboardLayout>
);
